import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  HomeIcon,
  DocumentDuplicateIcon,
  UserGroupIcon,
  CalendarIcon,
  Cog6ToothIcon,
  XMarkIcon,
  PlusIcon,
  ChatBubbleLeftIcon,
  FolderIcon,
  EnvelopeIcon,
  KeyIcon,
  UserCircleIcon,
  CheckCircleIcon,
  SunIcon,
  MoonIcon
} from '@heroicons/react/24/outline';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { collection, query, where, onSnapshot, orderBy, getDocs, addDoc, serverTimestamp, doc, getDoc } from 'firebase/firestore';
import { firestore, getInitializationPromise } from '../../firebase';
import CreateWorkspace from '../workspaces/CreateWorkspace';
import DualTimezoneClock from './DualTimezoneClock';
import { toast } from 'react-hot-toast';
import { useNotifications } from '../../contexts/NotificationsContext';
import { createPortal } from 'react-dom';

// Custom scrollbar styles
const scrollbarStyles = `
  .custom-scrollbar::-webkit-scrollbar {
    width: 6px;
  }
  
  .custom-scrollbar::-webkit-scrollbar-track {
    background: rgba(26, 26, 26, 0.5);
    border-radius: 10px;
  }
  
  .custom-scrollbar::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.2);
    border-radius: 10px;
  }
  
  .custom-scrollbar::-webkit-scrollbar-thumb:hover {
    background: rgba(255, 255, 255, 0.3);
  }
`;

// Add media query styles for mobile
const mobileStyles = `
  @media (max-width: 768px) {
    .mobile-calendar {
      padding: 0.5rem !important;
    }
    
    .mobile-calendar .react-calendar__month-view__days__day {
      padding: 0.25rem !important;
      height: 2rem !important;
      font-size: 0.75rem !important;
    }
    
    .mobile-calendar .react-calendar__month-view__weekdays__weekday {
      font-size: 0.7rem !important;
    }
    
    .mobile-calendar .react-calendar__navigation button {
      padding: 0.25rem !important;
      font-size: 0.9rem !important;
    }
    
    .custom-scrollbar {
      max-height: 80vh !important;
    }
    
    .mobile-hidden {
      display: none !important;
    }
  }
`;

// Define Blossom navigation items 
const blossomNavigation = [
  { name: 'Dashboard', href: '/', icon: HomeIcon },
  { name: 'Calendar', href: '/calendar', icon: CalendarIcon },
  { name: 'Docs', href: '/documents', icon: DocumentDuplicateIcon },
  { name: 'Chat', href: '/chat', icon: ChatBubbleLeftIcon },
];

// Define Personal navigation items
const personalNavigation = [
  { name: 'Calendar', href: '/personal/calendar', icon: CalendarIcon },
  { name: 'Docs', href: '/personal/documents', icon: DocumentDuplicateIcon },
];

// Add a StatusSelector component that uses Portal to render outside the sidebar
/**
 * StatusSelector Component - Version 2.1
 * 
 * Latest improvements:
 * - Direct DOM manipulation for immediate status changes
 * - Check both localStorage and React state for current status
 * - Visual indicator of active status
 * - Event dispatching for cross-component updates
 * - Explicit light/dark mode theme handling
 */
const StatusSelector = ({ isOpen, onClose, userProfile, updateUserStatus, position, setForceUpdateCallback, setLocalStatus, localUserStatus }) => {
  // Place hooks at the top level before any conditions
  const closeTimeoutRef = useRef(null);
  const { lightMode } = useTheme(); // Get theme context directly
  
  // Always initialize with the localStorage value first, then fallback
  useEffect(() => {
    const storedStatus = localStorage.getItem('bloomUserStatus');
    if (storedStatus && setLocalStatus) {
      setLocalStatus(storedStatus);
    }
  }, [setLocalStatus]);
  
  if (!isOpen) return null;
  
  const statusOptions = [
    { id: 'online', label: 'Online', icon: '🟢', color: 'text-green-500' },
    { id: 'away', label: 'Away', icon: '🟡', color: 'text-yellow-500' },
    { id: 'offline', label: 'Offline', icon: '⚪', color: 'text-gray-500' },
    { id: 'doNotDisturb', label: 'Do Not Disturb', icon: '🌙', color: '' },
    { id: 'sleeping', label: 'Sleeping', icon: '💤', color: '' },
    { id: 'inMeeting', label: 'In a Meeting', icon: '🎧', color: '' },
    { id: 'onVacation', label: 'On Vacation', icon: '🌴', color: '' },
    { id: 'sick', label: 'Sick', icon: '😷', color: '' }
  ];
  
  const handleStatusChange = (statusId) => {
    // Close menu immediately
      onClose();
      
    console.log(`Status changed to: ${statusId}`);
      
    // Update localStorage immediately
    localStorage.setItem('bloomUserStatus', statusId);
      
    // Find the selected status option
    const selectedStatus = statusOptions.find(option => option.id === statusId);
    if (!selectedStatus) return;
    
    // Update local state (React state)
    setLocalStatus(statusId);
      
    // Force UI refresh
    setForceUpdateCallback();
    
    // Direct DOM update - find all status indicators for current user and update them
    // This ensures the status is updated even if React doesn't re-render
    try {
      // Update sidebar status indicator
      const sidebarStatusIcon = document.querySelector('.status-emoji');
      if (sidebarStatusIcon) {
        sidebarStatusIcon.textContent = selectedStatus.icon;
        sidebarStatusIcon.style.color = selectedStatus.color || '';
      }
      
      // Update sidebar status text
      const statusText = document.querySelector('.status-text');
      if (statusText) {
        statusText.textContent = selectedStatus.label;
      }
      
      // Update status indicators in chat (current user only)
      const userId = localStorage.getItem('bloomUserId');
      if (userId) {
        const userChatMessages = document.querySelectorAll(`.message-${userId} .status-emoji`);
        userChatMessages.forEach(indicator => {
          indicator.textContent = selectedStatus.icon;
        });
        }
        
      // Alert the user of the status change
      // alert(`Status updated to: ${selectedStatus.label}`);
    } catch (error) {
      console.error('Error updating status DOM elements:', error);
        }
        
    // Broadcast event for other components to react
    const event = new CustomEvent('bloomStatusChanged', { 
      detail: { 
        status: statusId,
        statusIcon: selectedStatus.icon,
        statusLabel: selectedStatus.label,
        userId: localStorage.getItem('bloomUserId')
      } 
    });
    document.dispatchEvent(event);
        
    // Broadcast a refresh UI event
    const refreshEvent = new CustomEvent('bloomRefreshUI');
    document.dispatchEvent(refreshEvent);
    
    // Also try to update via API
    try {
      fetch('/api/users/status', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: statusId })
      })
      .then(response => {
        if (!response.ok) throw new Error('Failed to update status on server');
        return response.json();
      })
      .then(data => {
        console.log('Status updated on server:', data);
      })
      .catch(err => {
        console.error('Error updating status on server:', err);
      });
    } catch (error) {
      console.error('Error in status update API call:', error);
    }
  };
  
  // Calculate style for positioning
  const style = {
    position: 'absolute',
    top: position.top - 250, // Position well above the sidebar
    left: position.left + 213, // Position further to the right (increased from 100)
    zIndex: 9999
  };
  
  // Handle mouse enter and leave with delay
  const handleMouseEnter = () => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
  };

  const handleMouseLeave = () => {
    closeTimeoutRef.current = setTimeout(() => {
      onClose();
    }, 100); // Reduced from 300ms to 100ms for faster response
  };
  
  return createPortal(
    <div 
      style={{
        ...style,
        backgroundColor: lightMode ? 'white' : '#1a1a1a',
        borderColor: lightMode ? '#e5e7eb' : '#2a2a2a'
      }}
      className="w-48 rounded-lg shadow-lg overflow-hidden border"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <div className="py-1">
        {statusOptions.map((status) => {
          // Check if this status is active - use both localStorage and state to be sure
          const storedStatus = localStorage.getItem('bloomUserStatus');
          const isActive = localUserStatus === status.id || storedStatus === status.id;
          
          return (
            <button
              key={status.id}
              style={{
                backgroundColor: isActive 
                  ? (lightMode ? '#f9fafb' : '#252525') 
                  : 'transparent',
                color: lightMode ? '#374151' : '#e5e7eb'
              }}
              className="w-full px-3 py-2 flex items-center transition-colors font-medium"
              onMouseOver={(e) => {
                e.currentTarget.style.backgroundColor = lightMode ? '#f3f4f6' : '#252525';
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.backgroundColor = isActive 
                  ? (lightMode ? '#f9fafb' : '#252525') 
                  : 'transparent';
              }}
              onClick={() => handleStatusChange(status.id)}
            >
              <div className="w-5 flex justify-center">
                <div className="w-5 h-5 rounded-full bg-'transparent' flex items-center justify-center">
                <span className="text-base">{status.icon}</span>
              </div>
              </div>
              <span className="ml-2 text-sm">{status.label}</span>
              {isActive && (
                <span className="ml-auto text-blue-500">✓</span>
              )}
            </button>
          );
        })}
      </div>
    </div>,
    document.body
  );
};

const Sidebar = ({ isOpen, onClose }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { currentUser, logout, userProfile, updateUserProfile, updateUserStatus, setUserProfile } = useAuth();
  const { lightMode, toggleLightMode } = useTheme();
  const [workspaces, setWorkspaces] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isCreateWorkspaceOpen, setIsCreateWorkspaceOpen] = useState(false);
  const [refreshWorkspacesFlag, setRefreshWorkspacesFlag] = useState(0);
  const [showSettingsPanel, setShowSettingsPanel] = useState(false);
  const [showQuickSettings, setShowQuickSettings] = useState(false);
  const [showStatusSelector, setShowStatusSelector] = useState(false);
  const settingsRef = useRef(null);
  const statusSelectorRef = useRef(null);
  const [settingsFormData, setSettingsFormData] = useState({
    displayName: '',
    email: '',
    password: '',
    confirmPassword: '',
    status: 'online'
  });
  const [settingsLoading, setSettingsLoading] = useState(false);
  const [settingsError, setSettingsError] = useState('');
  const { unreadMessages, unreadWorkspaceMessages } = useNotifications();
  const [statusSelectorPosition, setStatusSelectorPosition] = useState({ top: 0, left: 0 });
  const statusOptionRef = useRef(null);
  const [forceUpdate, setForceUpdate] = useState(0);
  const [menuIsOpen, setMenuIsOpen] = useState(false);
  
  // Initialize with localStorage first, then fallback to userProfile
  const savedStatus = localStorage.getItem('bloomUserStatus');
  const [localUserStatus, setLocalUserStatus] = useState(savedStatus || userProfile?.status || 'online');
  
  // Ensure DOM is updated with the initial status on component load
  useEffect(() => {
    // Wait for the DOM to be fully rendered
    setTimeout(() => {
      // Helper function for updating status in DOM directly
      const updateStatusInDOM = (statusId) => {
        console.log('Initializing DOM status elements with:', statusId);
        
        // Update status text elements
        const statusTextElements = document.querySelectorAll('.sidebar-status-text');
        statusTextElements.forEach(el => {
          let newText = 'Online';
          switch (statusId) {
            case 'online': newText = 'Online'; break;
            case 'away': newText = 'Away'; break;
            case 'offline': newText = 'Offline'; break;
            case 'doNotDisturb': newText = 'Do Not Disturb'; break;
            case 'sleeping': newText = 'Sleeping'; break;
            case 'inMeeting': newText = 'In a Meeting'; break;
            case 'onVacation': newText = 'On Vacation'; break;
            case 'sick': newText = 'Sick'; break;
          }
          el.textContent = newText;
        });

        // Update status indicators
        const statusIndicators = document.querySelectorAll('.sidebar-status-indicator');
        statusIndicators.forEach(el => {
          // Clear existing content
          el.innerHTML = '';
          // Add new indicator
          const span = document.createElement('span');
          span.style.fontSize = '14px';
          switch (statusId) {
            case 'online': span.textContent = '🟢'; break;
            case 'away': span.textContent = '🟡'; break;
            case 'offline': span.textContent = '⚪'; break;
            case 'doNotDisturb': span.textContent = '🌙'; break;
            case 'sleeping': span.textContent = '💤'; break;
            case 'inMeeting': span.textContent = '🎧'; break;
            case 'onVacation': span.textContent = '🌴'; break;
            case 'sick': span.textContent = '😷'; break;
            default: span.textContent = '🟢';
          }
          el.appendChild(span);
        });
      };
      
      // First try to use localStorage status
      const storedStatus = localStorage.getItem('bloomUserStatus');
      if (storedStatus) {
        updateStatusInDOM(storedStatus);
      } else if (userProfile?.status) {
        // Fall back to user profile status
        updateStatusInDOM(userProfile.status);
      } else {
        // Default to online
        updateStatusInDOM('online');
      }
    }, 500); // Wait 500ms for DOM to be ready
  }, []);

  useEffect(() => {
    console.log('Initial status:', { 
      localStatus: localUserStatus, 
      savedStatus: localStorage.getItem('bloomUserStatus'),
      profileStatus: userProfile?.status 
    });
  }, []);

  // Helper function to get status text
  const getStatusText = (status) => {
    switch (status) {
      case 'online': return 'Online';
      case 'away': return 'Away';
      case 'offline': return 'Offline';
      case 'doNotDisturb': return 'Do Not Disturb';
      case 'sleeping': return 'Sleeping';
      case 'inMeeting': return 'In a Meeting';
      case 'onVacation': return 'On Vacation';
      case 'sick': return 'Sick';
      default: return 'Online';
    }
  };

  // Fetch workspaces from Firestore
  useEffect(() => {
    let unsubscribe = () => {};
    
    const fetchWorkspaces = async () => {
      if (!currentUser) return;
      
      setIsLoading(true);
      
      try {
        // Import firebase initialization if needed
        if (typeof getInitializationPromise === 'function') {
          await getInitializationPromise();
        }
        
        // Create listener for all workspaces, not filtering by membership
        const workspacesQuery = query(
          collection(firestore, 'workspaces'),
          orderBy('name', 'asc')
        );
        
        unsubscribe = onSnapshot(workspacesQuery, (snapshot) => {
          const updatedWorkspaces = snapshot.docs.map(doc => {
            const data = doc.data();
            return {
              id: doc.id,
              ...data,
              role: data.ownerId === currentUser.uid ? 'owner' : 'shared'
            };
          });
          
          setWorkspaces(updatedWorkspaces);
          setIsLoading(false);
        }, (error) => {
          console.error('Error in workspace listener:', error);
          setIsLoading(false);
          toast.error('Failed to load workspaces. Please refresh and try again.');
        });
      } catch (error) {
        console.error('Error setting up workspace listener:', error);
        setIsLoading(false);
        toast.error('Failed to load workspaces. Please refresh and try again.');
      }
    };
    
    fetchWorkspaces();
    
    return () => unsubscribe();
  }, [currentUser, refreshWorkspacesFlag]);

  // Listen for custom event to open the create workspace modal
  useEffect(() => {
    const handleOpenCreateWorkspaceModal = () => {
      setIsCreateWorkspaceOpen(true);
    };

    document.addEventListener('openCreateWorkspaceModal', handleOpenCreateWorkspaceModal);

    return () => {
      document.removeEventListener('openCreateWorkspaceModal', handleOpenCreateWorkspaceModal);
    };
  }, []);

  // Calculate offset to center the current hour
  useEffect(() => {
    if (currentUser) {
      setSettingsFormData(prev => ({
        ...prev,
        displayName: currentUser.displayName || '',
        email: currentUser.email || '',
        status: userProfile?.status || 'online'
      }));
    }
  }, [currentUser, userProfile]);

  // Add event listener to detect clicks outside the quick settings and status selectors
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (settingsRef.current && !settingsRef.current.contains(event.target)) {
        setShowQuickSettings(false);
      }
      if (statusSelectorRef.current && !statusSelectorRef.current.contains(event.target)) {
        setShowStatusSelector(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Add effect to close status selector when quick settings is closed
  useEffect(() => {
    if (!showQuickSettings) {
      setShowStatusSelector(false);
    }
  }, [showQuickSettings]);

  const isActive = (path) => {
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  const handleSignOut = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  // Handle modal close
  const handleModalClose = () => {
    console.log('Modal closed in Sidebar');
    
    // First, close the modal immediately
    setIsCreateWorkspaceOpen(false);
    
    // Then refresh workspaces to show any new workspaces
    // The refreshWorkspacesFlag triggers the useEffect to refetch workspaces
    setTimeout(() => {
    setRefreshWorkspacesFlag(prev => prev + 1);
    }, 500);
  };

  // Handle settings form change
  const handleSettingsChange = (e) => {
    const { name, value } = e.target;
    setSettingsFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Handle settings form submit
  const handleSettingsSubmit = async (e) => {
    e.preventDefault();
    setSettingsLoading(true);
    setSettingsError('');
    
    try {
      const { displayName, email, password, confirmPassword } = settingsFormData;
      
      // Validate inputs
      if (password && password !== confirmPassword) {
        throw new Error('Passwords do not match');
      }
      
      // Update display name if changed
      if (displayName !== currentUser?.displayName) {
        try {
          console.log('Updating display name to:', displayName);
          await updateUserProfile({ displayName });
          console.log('Display name updated successfully');
        } catch (err) {
          console.error('Failed to update display name:', err);
          throw new Error(`Failed to update display name: ${err.message}`);
        }
      }
      
      // Email and password changes would require re-authentication
      // Just show success for this demo
      toast.success('Settings updated successfully!');
      setShowSettingsPanel(false);
    } catch (error) {
      console.error('Settings update error:', error);
      setSettingsError(error.message);
    } finally {
      setSettingsLoading(false);
    }
  };

  // Settings Panel Component
  const SettingsPanel = () => (
    <div className="fixed inset-0 bg-black bg-opacity-75 z-50 flex items-center justify-center p-4" onClick={(e) => e.stopPropagation()}>
      <div className="bg-[#1a1a1a] rounded-lg w-full max-w-md border border-[#2a2a2a] shadow-xl">
        <div className="flex items-center justify-between border-b border-[#2a2a2a] p-4">
          <h2 className="text-lg font-medium text-white">User Settings</h2>
          <button 
            onClick={() => setShowSettingsPanel(false)} 
            className="text-gray-400 hover:text-white"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>
        
        <form onSubmit={handleSettingsSubmit} className="p-4">
          {settingsError && (
            <div className="mb-4 p-3 bg-red-900/20 border border-red-700 rounded text-red-400 text-sm">
              {settingsError}
            </div>
          )}
          
          {/* Light Mode Toggle */}
          <div className="mb-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <SunIcon className="h-5 w-5 text-yellow-400 mr-2" />
                <label className="text-sm font-medium text-gray-200">
                  Light Mode
                </label>
              </div>
              <button
                type="button"
                onClick={toggleLightMode}
                className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
                  lightMode ? 'bg-blue-600' : 'bg-gray-700'
                }`}
                role="switch"
                aria-checked={lightMode}
              >
                <span className="sr-only">Toggle light mode</span>
                <span
                  className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                    lightMode ? 'translate-x-5' : 'translate-x-0'
                  }`}
                />
              </button>
            </div>
            <p className="text-xs text-gray-500 mt-1 ml-7">Enable light theme for a brighter interface</p>
          </div>
          
          {/* Display Name */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-400 mb-1">
              Display Name
            </label>
            <div className="flex items-center">
              <UserCircleIcon className="h-5 w-5 text-gray-500 mr-2" />
              <input
                type="text"
                name="displayName"
                autoFocus
                value={settingsFormData.displayName}
                onChange={handleSettingsChange}
                className="w-full bg-[#252525] border border-[#333] rounded-md px-3 py-2 text-white focus:outline-none focus:border-white focus:ring-1 focus:ring-white"
                placeholder="Your display name"
              />
            </div>
          </div>
          
          {/* Email */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-400 mb-1">
              Email
            </label>
            <div className="flex items-center">
              <EnvelopeIcon className="h-5 w-5 text-gray-500 mr-2" />
              <input
                type="email"
                name="email"
                value={settingsFormData.email}
                onChange={handleSettingsChange}
                className="w-full bg-[#252525] border border-[#333] rounded-md px-3 py-2 text-white focus:outline-none focus:border-white focus:ring-1 focus:ring-white"
                placeholder="your@email.com"
                disabled
              />
            </div>
            <p className="text-xs text-gray-500 mt-1">Email changes require verification. Contact support.</p>
          </div>
          
          {/* Password */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-400 mb-1">
              New Password
            </label>
            <div className="flex items-center">
              <KeyIcon className="h-5 w-5 text-gray-500 mr-2" />
              <input
                type="password"
                name="password"
                value={settingsFormData.password}
                onChange={handleSettingsChange}
                className="w-full bg-[#252525] border border-[#333] rounded-md px-3 py-2 text-white focus:outline-none focus:border-white focus:ring-1 focus:ring-white"
                placeholder="Leave blank to keep current"
              />
            </div>
          </div>
          
          {/* Confirm Password */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-400 mb-1">
              Confirm Password
            </label>
            <div className="flex items-center">
              <KeyIcon className="h-5 w-5 text-gray-500 mr-2" />
              <input
                type="password"
                name="confirmPassword"
                value={settingsFormData.confirmPassword}
                onChange={handleSettingsChange}
                className="w-full bg-[#252525] border border-[#333] rounded-md px-3 py-2 text-white focus:outline-none focus:border-white focus:ring-1 focus:ring-white"
                placeholder="Confirm new password"
              />
            </div>
          </div>
          
          {/* Actions */}
          <div className="flex justify-end space-x-3">
            <button
              type="button"
              onClick={() => setShowSettingsPanel(false)}
              className="px-4 py-2 bg-transparent border border-[#333] text-gray-300 rounded-md hover:bg-[#252525]"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={settingsLoading}
              className="px-4 py-2 bg-[#2a2a2a] text-white rounded-md hover:bg-[#333] flex items-center border border-[#444]"
            >
              {settingsLoading ? (
                <>
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Saving...
                </>
              ) : (
                <>
                  <CheckCircleIcon className="h-4 w-4 mr-1" />
                  Save Changes
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );

  // Add a useEffect to log when userProfile changes
  useEffect(() => {
    console.log('userProfile changed in Sidebar:', userProfile);
  }, [userProfile]);

  // Add a dependency to force re-render when the status changes
  useEffect(() => {
    // This effect will run whenever forceUpdate changes
    console.log('Force update triggered:', forceUpdate);
  }, [forceUpdate]);

  // Add a useEffect to refresh the userProfile when forceUpdate changes
  useEffect(() => {
    const refreshUserProfile = async () => {
      if (!currentUser) return;
      
      try {
        console.log('Refreshing user profile...');
        const userDocRef = doc(firestore, 'users', currentUser.uid);
        const userSnapshot = await getDoc(userDocRef);
        
        if (userSnapshot.exists()) {
          const userData = userSnapshot.data();
          console.log('Refreshed user data:', userData);
          
          // This assumes there's a setUserProfile function in the Auth context
          // If there isn't, you might need to modify the AuthContext
          if (typeof updateUserProfile === 'function') {
            await updateUserProfile({
              ...userData
            });
            console.log('User profile refreshed successfully');
          }
        }
      } catch (error) {
        console.error('Error refreshing user profile:', error);
      }
    };
    
    refreshUserProfile();
  }, [forceUpdate, currentUser]);

  // Listen for status text DOM changes
  useEffect(() => {
    // Create a MutationObserver to watch for changes in status text
    const targetNode = document.querySelector('.status-text');
    if (targetNode) {
      console.log('Setting up MutationObserver for status-text');
      const observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          if (mutation.type === 'characterData' || mutation.type === 'childList') {
            console.log('Status text DOM changed, refreshing UI');
            setForceUpdate(prev => prev + 1);
          }
        });
      });

      observer.observe(targetNode, { 
        characterData: true, 
        childList: true,
        subtree: true 
      });

      return () => observer.disconnect();
    }

    return () => {};
  }, []);

  // Add a useEffect to listen for status changes in localStorage
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === 'bloomUserStatus') {
        console.log('STATUS UPDATE: Detected localStorage change to', e.newValue);
        // Force UI refresh when status changes in storage
        setLocalUserStatus(e.newValue);
        setForceUpdate(prev => prev + 1);
      }
    };
    
    // Listen for storage events (for cross-tab synchronization)
    window.addEventListener('storage', handleStorageChange);
    
    // Listen for custom event
    const handleStatusEvent = (e) => {
      console.log('STATUS UPDATE: Received bloomStatusChanged event', e.detail);
      setLocalUserStatus(e.detail.status);
      setForceUpdate(prev => prev + 1);
    };
    
    document.addEventListener('bloomStatusChanged', handleStatusEvent);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      document.removeEventListener('bloomStatusChanged', handleStatusEvent);
    };
  }, []);

  // Try to initialize localUserStatus from localStorage
  useEffect(() => {
    const storedStatus = localStorage.getItem('bloomUserStatus');
    if (storedStatus) {
      console.log('STATUS UPDATE: Initializing from localStorage:', storedStatus);
      setLocalUserStatus(storedStatus);
    } else if (userProfile?.status) {
      setLocalUserStatus(userProfile.status);
      localStorage.setItem('bloomUserStatus', userProfile.status);
    } else {
      // Default to sleeping
      setLocalUserStatus('sleeping');
      localStorage.setItem('bloomUserStatus', 'sleeping');
    }

    // Listen for status change events
    const handleStatusEvent = (e) => {
      const { status } = e.detail;
      if (status) {
        setLocalUserStatus(status);
        setForceUpdate(prev => prev + 1); // Force a refresh
      }
    };

    document.addEventListener('bloomStatusChanged', handleStatusEvent);
    
    return () => {
      document.removeEventListener('bloomStatusChanged', handleStatusEvent);
    };
  }, [userProfile?.status]);

  // Add a useEffect to log status updates
  useEffect(() => {
    console.log('localUserStatus updated:', localUserStatus);
    console.log('userProfile.status:', userProfile?.status);
  }, [localUserStatus, userProfile]);

  // In the Sidebar component, add a direct status update function to test
  const testStatusUpdate = (status) => {
    // Call directly to API without any middleware
    console.log(`DIRECT TEST: Calling updateUserStatus("${status}")`);
    updateUserStatus(status)
      .then(result => {
        console.log('DIRECT TEST RESULT:', result);
        toast.success(`Direct status update result: ${result}`);
        // Force UI refresh
        setForceUpdate(prev => prev + 1);
      })
      .catch(error => {
        console.error('DIRECT TEST ERROR:', error);
        toast.error(`Direct test error: ${error.message}`);
      });
  };

  // Add an additional effect to synchronize with Chat components
  useEffect(() => {
    // Create a global function to update status that Chat can access
    window.updateBloomStatus = (status) => {
      console.log('GLOBAL: External status update to', status);
      setLocalUserStatus(status);
      setForceUpdate(prev => prev + 1);
      return true;
    };
    
    // Expose the current status globally
    window.getCurrentBloomStatus = () => {
      return localUserStatus || userProfile?.status || 'online';
    };
    
    return () => {
      delete window.updateBloomStatus;
      delete window.getCurrentBloomStatus;
    };
  }, [localUserStatus, userProfile]);

  // Initialize localUserStatus with a default if userProfile is not yet loaded
  useEffect(() => {
    if (!localUserStatus) {
      setLocalUserStatus(userProfile?.status || 'online');
    }
  }, []);

  // Add a global test function that can be called from the console
  useEffect(() => {
    window.testStatusUpdate = (status) => {
      console.log(`CONSOLE TEST: Setting status to "${status}"`);
      
      // First try to update directly through the API
      updateUserStatus(status)
        .then(success => {
          console.log(`Status update API call returned: ${success}`);
          
          if (success) {
            // Update local state regardless of API result
            setLocalUserStatus(status);
            
            // Force component update
            setForceUpdate(prev => prev + 1);
            
            // Also update localStorage
            localStorage.setItem('bloomUserStatus', status);
            
            // Dispatch custom event
            document.dispatchEvent(new CustomEvent('bloomStatusChanged', { 
              detail: { status: status }
            }));
            
            return `Status successfully updated to ${status}`;
          } else {
            return `Failed to update status to ${status}`;
          }
        })
        .catch(error => {
          console.error('Test status update error:', error);
          return `Error: ${error.message}`;
        });
        
      return `Test function executed. Check console for results.`;
    };
    
    return () => {
      delete window.testStatusUpdate;
    };
  }, [updateUserStatus]);

  // Force a DOM update whenever localUserStatus changes
  useEffect(() => {
    // Define the update function
    const updateStatusDisplay = (status) => {
      console.log('Forcing DOM update for status:', status);
      
      // Update text elements
      const statusTextElements = document.querySelectorAll('.sidebar-status-text');
      statusTextElements.forEach(el => {
        el.textContent = getStatusText(status);
      });

      // Update indicators
      const statusIndicators = document.querySelectorAll('.sidebar-status-indicator');
      statusIndicators.forEach(el => {
        // Clear existing content
        el.innerHTML = '';
        // Add new indicator
        const span = document.createElement('span');
        span.style.fontSize = '14px';
        switch (status) {
          case 'online': span.textContent = '🟢'; break;
          case 'away': span.textContent = '🟡'; break;
          case 'offline': span.textContent = '⚪'; break;
          case 'doNotDisturb': span.textContent = '🌙'; break;
          case 'sleeping': span.textContent = '💤'; break;
          case 'inMeeting': span.textContent = '🎧'; break;
          case 'onVacation': span.textContent = '🌴'; break;
          case 'sick': span.textContent = '😷'; break;
          default: span.textContent = '🟢';
        }
        el.appendChild(span);
      });
      
      // Also update status in statusOptionRef element if it exists
      if (statusOptionRef.current) {
        const statusIcon = statusOptionRef.current.querySelector('.text-base.mr-2');
        if (statusIcon) {
          let icon = '🟢';
          switch (status) {
            case 'online': icon = '🟢'; break;
            case 'away': icon = '🟡'; break;
            case 'offline': icon = '⚪'; break;
            case 'doNotDisturb': icon = '🌙'; break;
            case 'sleeping': icon = '💤'; break;
            case 'inMeeting': icon = '🎧'; break;
            case 'onVacation': icon = '🌴'; break;
            case 'sick': icon = '😷'; break;
          }
          statusIcon.textContent = icon;
        }
      }

      // Update DOM data attributes
      document.documentElement.setAttribute('data-user-status', status);
    };
    
    // Call the update function with current status
    if (localUserStatus) {
      updateStatusDisplay(localUserStatus);
    }
  }, [localUserStatus]);

  // Create a global function for direct status changes
  useEffect(() => {
    window.setStatus = (status) => {
      console.log('🌟 GLOBAL STATUS CHANGE:', status);
      
      // 1. Update localStorage 
      localStorage.setItem('bloomUserStatus', status);
      
      // 2. Update state
      setLocalUserStatus(status);
      
      // 3. Force UI refresh
      setForceUpdate(prev => prev + 1);
      
      // 4. Notify all components
      document.dispatchEvent(new CustomEvent('bloomStatusChanged', { 
        detail: { status } 
      }));
      
      // 5. Try to update auth context
      if (typeof updateUserStatus === 'function') {
        updateUserStatus(status).catch(err => {
          console.error('Failed to update status in auth context:', err);
        });
      }
      
      // Done
      return `Status changed to: ${status}`;
    };
    
    return () => {
      delete window.setStatus;
    };
  }, [updateUserStatus, setLocalUserStatus]);

  // Add effect to log theme changes
  useEffect(() => {
    console.log('Theme mode changed:', lightMode ? 'light' : 'dark');
  }, [lightMode]);

  // Store current user ID in localStorage for status updates
  useEffect(() => {
    if (currentUser?.uid) {
      localStorage.setItem('bloomUserId', currentUser.uid);
    }
  }, [currentUser]);

  return (
    <>
      {/* Mobile backdrop */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-gray-600 bg-opacity-75 z-20 lg:hidden"
          onClick={onClose}
        />
      )}

      {/* Sidebar */}
      <div
        className={`fixed inset-y-0 left-0 z-30 w-64 bg-[#121212] border border-[#1e1e1e] transform transition-transform duration-300 ease-in-out ${
          isOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
        } rounded-2xl m-2`}
        style={{ width: '15.5rem', maxWidth: 'calc(100vw - 1rem)' }}
      >
        <style>
          {scrollbarStyles}
          {mobileStyles}
        </style>
        <div className="h-full flex flex-col">
          {/* Sidebar header */}
          <div className="flex items-center h-16 px-4">
            <Link to="/" className="flex items-center">
              <img
                src={lightMode ? '/logolight.svg' : '/logo.svg'}
                alt="Blossom Logo"
                className="w-6 h-6 mr-2"
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = 'data:image/svg+xml;base64,PHN2ZyB2aWV3Qm94PSIwIDAgMjQgMjQiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PHJlY3QgeD0iNCIgeT0iNCIgd2lkdGg9IjE2IiBoZWlnaHQ9IjE2IiByeD0iMiIgc3Ryb2tlPSJ3aGl0ZSIgc3Ryb2tlLXdpZHRoPSIyIiAvPjxwYXRoIGQ9Ik0xMiA0IFYyMCIgc3Ryb2tlPSJ3aGl0ZSIgc3Ryb2tlLXdpZHRoPSIyIiAvPjxwYXRoIGQ9Ik00IDEyIEgyMCIgc3Ryb2tlPSJ3aGl0ZSIgc3Ryb2tlLXdpZHRoPSIyIiAvPjwvc3ZnPg==';
                }}
              />
              <span className="text-xl font-semibold text-white">
                Blossom
              </span>
            </Link>
            <div className="flex items-center ml-auto">
              <button
                onClick={onClose}
                className="lg:hidden text-gray-400 hover:text-gray-300"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>
          </div>

          {/* Navigation */}
          <nav className="flex-1 overflow-y-auto px-3 py-4">
            {/* Main Blossom navigation */}
            <div className="space-y-1 mb-6">
              {blossomNavigation.map((item) => {
                const active = isActive(item.href);
                return (
                  <Link
                    key={item.name}
                    to={item.href}
                    className={`group flex items-center px-2 py-2 text-sm font-medium rounded-md ${
                      active
                        ? 'bg-[#1a1a1a] text-white'
                        : 'text-gray-300 hover:bg-[#1a1a1a] hover:text-white'
                    }`}
                  >
                    <item.icon
                      className={`mr-3 h-6 w-6 ${
                        active
                          ? 'text-gray-300'
                          : 'text-gray-400 group-hover:text-gray-300'
                      }`}
                    />
                    {item.name}
                    
                    {/* Add notification badge for chat */}
                    {item.name === 'Chat' && unreadMessages > 0 && (
                      <span className="ml-auto flex h-5 w-5 items-center justify-center rounded-full bg-white text-black text-xs">
                        {unreadMessages > 9 ? '9+' : unreadMessages}
                      </span>
                    )}
                  </Link>
                );
              })}
            </div>
            
            {/* Workspaces section */}
            <div className="mb-6">
              <div className="flex justify-between items-center mb-2">
                <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider px-2">
                  WORKSPACES
                </h3>
                <button 
                  className="text-gray-400 hover:text-white p-1 rounded-full hover:bg-[#1a1a1a] transition-colors duration-200 hidden md:block"
                  onClick={() => setIsCreateWorkspaceOpen(true)}
                  title="Create new workspace"
                  aria-label="Create new workspace"
                >
                  <PlusIcon className="h-4 w-4" />
                </button>
              </div>
              
              <div className="mt-1 space-y-1 max-h-[calc(100vh-500px)] overflow-y-auto pr-1 custom-scrollbar">
                {!isLoading ? (
                  workspaces.map((workspace) => {
                    const active = isActive(`/workspaces/${workspace.id}`);
                    const unreadCount = unreadWorkspaceMessages[workspace.id] || 0;
                    
                    return (
                      <Link
                        key={workspace.id}
                        to={`/workspaces/${workspace.id}`}
                        className={`group flex items-center px-2 py-2 text-sm font-medium rounded-md relative ${
                          active
                            ? 'bg-[#1a1a1a] text-white'
                            : 'text-gray-300 hover:bg-[#1a1a1a] hover:text-white'
                        }`}
                      >
                        <FolderIcon
                          className={`mr-3 h-5 w-5 ${
                            active
                              ? 'text-gray-300'
                              : 'text-gray-400 group-hover:text-gray-300'
                          }`}
                        />
                        <span className="truncate">{workspace.name}</span>
                        
                        {/* Display unread count for workspace */}
                        {unreadCount > 0 && (
                          <span className="ml-auto flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-white text-black text-xs">
                            {unreadCount > 9 ? '9+' : unreadCount}
                          </span>
                        )}
                      </Link>
                    );
                  })
                ) : (
                  // Loading placeholders
                  [...Array(3)].map((_, i) => (
                    <div
                      key={i}
                      className="px-2 py-2 rounded-md animate-pulse bg-[#1a1a1a] mb-1"
                    >
                      <div className="h-4 w-2/3 bg-[#333] rounded"></div>
                    </div>
                  ))
                )}
              </div>
            </div>

            {/* Personal section */}
            <div className="mb-6">
              <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider px-2 mb-2">
                PERSONAL
              </h3>
              <div className="space-y-1">
                {personalNavigation.map((item) => {
                  const active = isActive(item.href);
                  return (
                    <Link
                      key={item.name}
                      to={item.href}
                      className={`group flex items-center px-2 py-2 text-sm font-medium rounded-md ${
                        active
                          ? 'bg-[#1a1a1a] text-white'
                          : 'text-gray-300 hover:bg-[#1a1a1a] hover:text-white'
                      }`}
                    >
                      <item.icon
                        className={`mr-3 h-6 w-6 ${
                          active
                            ? 'text-gray-300'
                            : 'text-gray-400 group-hover:text-gray-300'
                        }`}
                      />
                      {item.name}
                    </Link>
                  );
                })}
              </div>
            </div>

            {/* Dual Timezone Clock */}
            <div className="mt-auto mb-4">
              <DualTimezoneClock 
                secondaryTimezone="Europe/Madrid"
                primaryTimezone="America/Chicago"
              />
            </div>
          </nav>

          {/* User profile */}
          <div className="border-t border-[#1e1e1e] p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 p-3">
                <div className="relative">
                  <div className="w-10 h-10 rounded-full flex items-center justify-center bg-white dark:bg-[#121212] text-black dark:text-white font-medium text-lg">
                    {userProfile?.firstName ? userProfile?.firstName.charAt(0).toUpperCase() : (currentUser?.displayName ? currentUser.displayName.charAt(0).toUpperCase() : 'U')}
                  </div>
                  <div className="absolute -right-1 -bottom-1 bg-white dark:bg-'transparent' rounded-full p-[2px]">
                    <div className="w-4 h-4 rounded-full flex items-center justify-center bg-'transparent' ">
                      <span className="status-emoji" style={{ fontSize: '14px' }}>
                        {(() => {
                          const storedStatus = localStorage.getItem('bloomUserStatus');
                          const status = storedStatus || userProfile?.status || 'sleeping';
                          
                          switch (status) {
                            case 'online': return '🟢';
                            case 'busy': return '🔴';
                            case 'away': return '🟠';
                            case 'meeting': return '📅';
                            case 'commuting': return '🚗';
                            case 'sick': return '🤒';
                            case 'vacation': return '🏖️';
                            case 'working': return '💻';
                            case 'focus': return '🎯';
                            default: return '😴'; // sleeping
                          }
                        })()}
                      </span>
                  </div>
                </div>
                </div>
                <div className="flex flex-col">
                  <span className="text-white text-sm font-medium">{userProfile?.firstName || currentUser?.displayName || 'User'}</span>
                  <span className="text-gray-400 text-xs status-text">
                    {(() => {
                      const storedStatus = localStorage.getItem('bloomUserStatus');
                      const status = storedStatus || userProfile?.status || 'sleeping';
                      
                      switch (status) {
                        case 'online': return 'Online';
                        case 'busy': return 'Busy';
                        case 'away': return 'Away';
                        case 'meeting': return 'In a Meeting';
                        case 'commuting': return 'Commuting';
                        case 'sick': return 'Out sick';
                        case 'vacation': return 'On vacation';
                        case 'working': return 'Working';
                        case 'focus': return 'Focusing';
                        default: return 'Sleeping';
                      }
                    })()}
                  </span>
                </div>
              </div>
              
              <div className="flex items-center relative" ref={settingsRef}>
                <button 
                  onClick={() => setShowQuickSettings(!showQuickSettings)}
                  className="text-gray-400 hover:text-white p-2 rounded-full hover:bg-[#2a2a2a]"
                >
                  <Cog6ToothIcon className="h-5 w-5" />
                </button>
                
                {/* Quick Settings Dropdown */}
                {showQuickSettings && (
                  <div 
                    style={{
                      backgroundColor: lightMode ? 'white' : '#1a1a1a',
                      borderColor: lightMode ? '#e5e7eb' : '#2a2a2a'
                    }}
                    className="absolute bottom-full right-0 mb-2 w-56 rounded-md shadow-lg border overflow-hidden z-10"
                  >
                    <div className="p-2">
                      {/* Change Status Option with hover submenu */}
                      <div 
                        ref={statusOptionRef}
                        style={{
                          backgroundColor: 'transparent',
                          color: lightMode ? '#374151' : 'white'
                        }}
                        className="flex items-center p-2 rounded-md cursor-pointer"
                        onMouseOver={(e) => {
                          e.currentTarget.style.backgroundColor = lightMode ? '#f3f4f6' : '#252525';
                        }}
                        onMouseOut={(e) => {
                          e.currentTarget.style.backgroundColor = 'transparent';
                        }}
                        onClick={() => {
                          // Toggle menu and initialize with current status
                          const storedStatus = localStorage.getItem('bloomUserStatus');
                          setLocalUserStatus(storedStatus || userProfile?.status || 'sleeping');
                          setMenuIsOpen(!menuIsOpen);
                        }}
                      >
                        <div className="w-5 h-5 rounded-full bg-'transparent'' flex items-center justify-center mr-2">
                          <span className="status-emoji" style={{ fontSize: '14px' }}>
                            {(() => {
                              const storedStatus = localStorage.getItem('bloomUserStatus');
                              const status = storedStatus || userProfile?.status || 'sleeping';
                              
                              switch (status) {
                                case 'online': return '🟢';
                                case 'busy': return '🔴';
                                case 'away': return '🟠';
                                case 'meeting': return '📅';
                                case 'commuting': return '🚗';
                                case 'sick': return '🤒';
                                case 'vacation': return '🏖️';
                                case 'working': return '💻';
                                case 'focus': return '🎯';
                                default: return '😴'; // sleeping
                              }
                            })()}
                        </span>
                        </div>
                        <span className="text-sm">Set Status</span>
                      </div>
                      
                      {/* Light Mode Toggle */}
                      <div 
                        style={{
                          backgroundColor: 'transparent',
                          color: lightMode ? '#374151' : 'white'
                        }}
                        className="flex items-center justify-between p-2 rounded-md cursor-pointer"
                        onMouseOver={(e) => {
                          e.currentTarget.style.backgroundColor = lightMode ? '#f3f4f6' : '#252525';
                        }}
                        onMouseOut={(e) => {
                          e.currentTarget.style.backgroundColor = 'transparent';
                        }}
                        onClick={toggleLightMode}
                      >
                        <div className="flex items-center">
                          {lightMode ? (
                            <MoonIcon className="h-5 w-5 text-gray-600 mr-2" />
                          ) : (
                            <SunIcon className="h-5 w-5 text-yellow-400 mr-2" />
                          )}
                          <span className="text-sm">{lightMode ? 'Dark Mode' : 'Light Mode'}</span>
                        </div>
                        <div 
                          className={`relative inline-flex h-5 w-9 flex-shrink-0 rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out ${
                            lightMode ? 'bg-blue-600' : 'bg-gray-700'
                          }`}
                        >
                          <span
                            className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                              lightMode ? 'translate-x-4' : 'translate-x-0'
                            }`}
                          />
                        </div>
                      </div>
                      
                      {/* Settings Link */}
                      <div 
                        style={{
                          backgroundColor: 'transparent',
                          color: lightMode ? '#374151' : 'white'
                        }}
                        className="flex items-center p-2 rounded-md cursor-pointer"
                        onMouseOver={(e) => {
                          e.currentTarget.style.backgroundColor = lightMode ? '#f3f4f6' : '#252525';
                        }}
                        onMouseOut={(e) => {
                          e.currentTarget.style.backgroundColor = 'transparent';
                        }}
                        onClick={() => {
                          setShowQuickSettings(false);
                          setShowSettingsPanel(true);
                        }}
                      >
                        <Cog6ToothIcon className="h-5 w-5 mr-2" style={{color: lightMode ? '#6b7280' : '#9ca3af'}} />
                        <span className="text-sm">Settings</span>
                      </div>
                      
                      {/* Logout */}
                      <div 
                        style={{
                          backgroundColor: 'transparent',
                          color: lightMode ? '#374151' : 'white'
                        }}
                        className="flex items-center p-2 rounded-md cursor-pointer"
                        onMouseOver={(e) => {
                          e.currentTarget.style.backgroundColor = lightMode ? '#f3f4f6' : '#252525';
                        }}
                        onMouseOut={(e) => {
                          e.currentTarget.style.backgroundColor = 'transparent';
                        }}
                        onClick={handleSignOut}
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-red-400 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                        </svg>
                        <span className="text-sm">Logout</span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* CreateWorkspace Modal */}
      <CreateWorkspace 
        isOpen={isCreateWorkspaceOpen} 
        onClose={handleModalClose}
      />

      {/* Settings Panel */}
      {showSettingsPanel && (
        <SettingsPanel />
      )}

      {/* Status Selector */}
      {menuIsOpen && (
      <StatusSelector 
          isOpen={true}
          position={{ top: statusOptionRef.current?.getBoundingClientRect()?.top || 0, left: statusOptionRef.current?.getBoundingClientRect()?.left || 0 }}
          onClose={() => setMenuIsOpen(false)}
        userProfile={userProfile}
          updateUserStatus={updateUserStatus}
          setForceUpdateCallback={() => setForceUpdate({})}
          setLocalStatus={setLocalUserStatus}
          localUserStatus={localUserStatus}
        />
      )}

      {/* Add floating action button for mobile to create workspace */}
      <div className="md:hidden fixed bottom-20 right-4 z-50">
        <button
          onClick={() => setIsCreateWorkspaceOpen(true)}
          className="bg-[#2a2a2a] text-white w-12 h-12 rounded-full shadow-lg flex items-center justify-center"
          title="Create new workspace"
          aria-label="Create new workspace"
        >
          <PlusIcon className="h-6 w-6" />
        </button>
      </div>

      {/* Hidden Debug Panel - only visible with ?debug=true in URL */}
      {window.location.search.includes('debug=true') && (
        <div className="fixed bottom-0 left-0 bg-black bg-opacity-80 text-white p-2 text-xs z-50 w-full">
          <div>Debug - Status Info:</div>
          <div>Local: {localUserStatus} | Profile: {userProfile?.status} | Stored: {localStorage.getItem('bloomUserStatus')}</div>
          <div className="flex mt-1 space-x-2">
            <button 
              onClick={() => {
                // Use direct DOM manipulation for reliability
                const status = 'online';
                
                // Update localStorage
          localStorage.setItem('bloomUserStatus', status);
          
                // Update state
                setLocalUserStatus(status);
                
                // Force UI refresh
            setForceUpdate(prev => prev + 1);
            
                // Direct DOM updates to all status indicators
                const statusIndicators = document.querySelectorAll('.sidebar-status-indicator');
                statusIndicators.forEach(indicator => {
                  indicator.innerHTML = '<span style="font-size:14px">🟢</span>';
                });
                
                // Direct DOM updates to all status text elements
                const statusTexts = document.querySelectorAll('.sidebar-status-text');
                statusTexts.forEach(text => {
                  text.textContent = 'Online';
                });
                
                // Broadcast event
                document.dispatchEvent(new CustomEvent('bloomStatusChanged', { detail: { status } }));
                
                // Try to update via API
                updateUserStatus(status).catch(console.error);
                
                alert('Status set to Online');
              }}
              className="px-2 py-1 bg-green-800 rounded text-white text-xs"
            >
              Set Online
            </button>
            <button 
              onClick={() => {
                // Use direct DOM manipulation for reliability
                const status = 'away';
                
                // Update localStorage
                localStorage.setItem('bloomUserStatus', status);
                
                // Update state
                setLocalUserStatus(status);
                
                // Force UI refresh
          setForceUpdate(prev => prev + 1);
                
                // Direct DOM updates to all status indicators
                const statusIndicators = document.querySelectorAll('.sidebar-status-indicator');
                statusIndicators.forEach(indicator => {
                  indicator.innerHTML = '<span style="font-size:14px">🟡</span>';
                });
                
                // Direct DOM updates to all status text elements
                const statusTexts = document.querySelectorAll('.sidebar-status-text');
                statusTexts.forEach(text => {
                  text.textContent = 'Away';
                });
                
                // Broadcast event
                document.dispatchEvent(new CustomEvent('bloomStatusChanged', { detail: { status } }));
                
                // Try to update via API
                updateUserStatus(status).catch(console.error);
                
                alert('Status set to Away');
              }}
              className="px-2 py-1 bg-yellow-800 rounded text-white text-xs"
            >
              Set Away
            </button>
            <button 
              onClick={() => {
                // Use direct DOM manipulation for reliability
                const status = 'offline';
                
                // Update localStorage
                localStorage.setItem('bloomUserStatus', status);
                
                // Update state
                setLocalUserStatus(status);
                
                // Force UI refresh
                setForceUpdate(prev => prev + 1);
                
                // Direct DOM updates to all status indicators
                const statusIndicators = document.querySelectorAll('.sidebar-status-indicator');
                statusIndicators.forEach(indicator => {
                  indicator.innerHTML = '<span style="font-size:14px">⚪</span>';
                });
                
                // Direct DOM updates to all status text elements
                const statusTexts = document.querySelectorAll('.sidebar-status-text');
                statusTexts.forEach(text => {
                  text.textContent = 'Offline';
                });
                
                // Broadcast event
                document.dispatchEvent(new CustomEvent('bloomStatusChanged', { detail: { status } }));
                
                // Try to update via API
                updateUserStatus(status).catch(console.error);
                
                alert('Status set to Offline');
              }}
              className="px-2 py-1 bg-gray-800 rounded text-white text-xs"
            >
              Set Offline
            </button>
        <button
              onClick={() => {
                // Force refresh from localStorage
                const saved = localStorage.getItem('bloomUserStatus');
                if (saved) {
                  setLocalUserStatus(saved);
                  setForceUpdate(prev => prev + 1);
                  
                  // Direct DOM updates to ensure UI is updated
                  const statusIndicators = document.querySelectorAll('.sidebar-status-indicator');
                  const statusTexts = document.querySelectorAll('.sidebar-status-text');
                  
                  let emoji = '🟢';
                  let text = 'Online';
                  
                  switch(saved) {
                    case 'online': emoji = '🟢'; text = 'Online'; break;
                    case 'away': emoji = '🟡'; text = 'Away'; break;
                    case 'offline': emoji = '⚪'; text = 'Offline'; break;
                    case 'doNotDisturb': emoji = '🌙'; text = 'Do Not Disturb'; break;
                    case 'sleeping': emoji = '💤'; text = 'Sleeping'; break;
                    case 'inMeeting': emoji = '🎧'; text = 'In a Meeting'; break;
                    case 'onVacation': emoji = '🌴'; text = 'On Vacation'; break;
                    case 'sick': emoji = '😷'; text = 'Sick'; break;
                  }
                  
                  statusIndicators.forEach(indicator => {
                    indicator.innerHTML = `<span style="font-size:14px">${emoji}</span>`;
                  });
                  
                  statusTexts.forEach(textElement => {
                    textElement.textContent = text;
                  });
                  
                  alert(`Status refreshed to ${text}`);
                }
              }}
              className="px-2 py-1 bg-blue-800 rounded text-white text-xs"
            >
              Refresh
        </button>
      </div>
        </div>
      )}
    </>
  );
};

export default Sidebar; 